/** Outcome of one comanda station's print attempt during the fan-out. */
export interface IComandaStationOutcome {
  /**
   * EVERY order-item index routed to the station → units credited as printed
   * (or possibly printed) at that station in this run. 0 = certainly nothing
   * (retry-safe failure before the document, missing printer, nothing
   * printable). A fully printed station credits each line its passed amount.
   */
  creditedUnits: ReadonlyMap<number, number>;
}

/**
 * Aggregates per-station comanda outcomes into the units that can be marked
 * as printed: per index, the MINIMUM credited units across every outcome
 * containing it; entries at 0 are dropped. A line shared across stations
 * where one station credited nothing therefore stays unmarked so catch-up
 * can retry it — accepting a bounded duplicate at the stations that already
 * printed, because a silent loss is worse than a visible duplicate. Unit
 * granularity (not a boolean per index) keeps a station that printed 3 of 5
 * unit documents from re-printing those 3 on every catch-up pass.
 */
export function computeMarkableComandaUnits(
  outcomes: IComandaStationOutcome[]
): Map<number, number> {
  const markable = new Map<number, number>();

  for (const outcome of outcomes) {
    for (const [index, units] of outcome.creditedUnits) {
      markable.set(index, Math.min(markable.get(index) ?? Infinity, units));
    }
  }

  for (const [index, units] of markable) {
    if (units <= 0) markable.delete(index);
  }

  return markable;
}
